// =========================================================================
// Progress (localStorage 最小保存)
// =========================================================================
// spec/closing_river_web_mvp_v2.md §「Progress」 / §「画面フロー」
//
// 担当: W7 (title + list + progress)
//
// 役割:
//   - localStorage に Tutorial 完走フラグと board ごとの best badge を保存。
//   - 同 board の再プレイで badge が改善したら上書き (`bestMoves` は最小、
//     `silent`/`clean`/`cleared` は OR 累積)。
//   - localStorage が使えない環境 (e.g. iOS Safari private mode) では
//     sessionStorage を試し、それも失敗時は in-memory fallback。
//
// 絶対則:
//   - Catalog の field を spec 外で汚染しない。`completedBoards[boardId]` に
//     spec の BestBadge field 以外を入れない。
//
// 触ってはいけない:
//   - localStorage の key は `nagi-closing-river-progress-v1` で固定 (spec)。
//     schema 進化時は v2 key を別に切る。
// =========================================================================

// -------------------------------------------------------------------------
// 型 (catalog/manifest.ts の Badge と互換、独立に定義して循環依存を避ける)
// -------------------------------------------------------------------------

/**
 * board ごとの「ベスト評価」。spec §「Progress」 そのまま。
 * - `cleared` / `clean` / `silent` は OR 累積 (一度でも達成したら true で固定)
 * - `bestMoves` は Silent 達成時のみ書き、複数回 Silent 達成時は最小手数を保持
 */
export interface BestBadge {
  readonly cleared: boolean;
  readonly clean: boolean;
  readonly silent: boolean;
  /** Silent 達成時のみ (= shortest と等しい movesUsed) */
  readonly bestMoves?: number;
}

/**
 * localStorage に保存する全体の構造。
 * codex R5 errata: `completedSeeds` → `completedBoards` rename 済み (boardId キー)。
 */
export interface ProgressState {
  readonly tutorialSeen: boolean;
  readonly completedBoards: Readonly<Record<string, BestBadge>>;
}

// -------------------------------------------------------------------------
// 定数
// -------------------------------------------------------------------------

const STORAGE_KEY = 'nagi-closing-river-progress-v1';

const EMPTY_STATE: ProgressState = Object.freeze({
  tutorialSeen: false,
  completedBoards: Object.freeze({}),
});

// -------------------------------------------------------------------------
// Storage backend (localStorage → sessionStorage → in-memory のフォールバック)
// -------------------------------------------------------------------------

interface StorageBackend {
  readonly kind: 'localStorage' | 'sessionStorage' | 'memory';
  read(): string | null;
  write(value: string): void;
}

function tryWebStorage(
  kind: 'localStorage' | 'sessionStorage',
  storage: Storage,
): StorageBackend | null {
  // private browsing 等で setItem が throw するケースを検出するためにプローブを書き込む。
  try {
    const probe = `${STORAGE_KEY}__probe`;
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return {
      kind,
      read() {
        try {
          return storage.getItem(STORAGE_KEY);
        } catch {
          return null;
        }
      },
      write(value) {
        try {
          storage.setItem(STORAGE_KEY, value);
        } catch (err) {
          // 容量オーバー / iCloud 同期エラー等は無視 (memory fallback には移行しない:
          // セッション中の見かけ上の挙動を一定に保つため、書き込み失敗だけ警告)
          // eslint-disable-next-line no-console
          console.warn('[nagi/progress] storage write failed:', err);
        }
      },
    };
  } catch {
    return null;
  }
}

function resolveBackend(): StorageBackend {
  if (typeof window === 'undefined') {
    // SSR / Node から呼ばれた場合の安全網 (このアプリは SPA だが念のため)
    return memoryBackend();
  }
  try {
    if (window.localStorage) {
      const ls = tryWebStorage('localStorage', window.localStorage);
      if (ls) return ls;
    }
  } catch {
    // window.localStorage 自体へのアクセスが SecurityError になる環境 (古い Safari)
  }
  try {
    if (window.sessionStorage) {
      const ss = tryWebStorage('sessionStorage', window.sessionStorage);
      if (ss) {
        // eslint-disable-next-line no-console
        console.warn(
          '[nagi/progress] localStorage unavailable, falling back to sessionStorage',
        );
        return ss;
      }
    }
  } catch {
    /* fallthrough */
  }
  // eslint-disable-next-line no-console
  console.warn(
    '[nagi/progress] localStorage and sessionStorage unavailable, using in-memory fallback (progress will not persist)',
  );
  return memoryBackend();
}

function memoryBackend(): StorageBackend {
  let buf: string | null = null;
  return {
    kind: 'memory',
    read() {
      return buf;
    },
    write(value) {
      buf = value;
    },
  };
}

// backend は一度だけ resolve (test 用に reset したい場合は __resetForTest を使う)
let backend: StorageBackend | null = null;
function getBackend(): StorageBackend {
  if (backend === null) {
    backend = resolveBackend();
  }
  return backend;
}

// -------------------------------------------------------------------------
// 公開 API
// -------------------------------------------------------------------------

/**
 * localStorage から ProgressState を読む。
 * - 未保存 / 壊れた JSON / schema 不整合 → EMPTY_STATE を返す (warn 出力)
 * - 同期処理 (call site で await 不要)
 */
export function load(): ProgressState {
  const raw = getBackend().read();
  if (raw === null) return EMPTY_STATE;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[nagi/progress] failed to parse stored JSON, resetting:', err);
    return EMPTY_STATE;
  }
  return normalizeProgressState(parsed);
}

/**
 * ProgressState を localStorage に保存する。
 * - 失敗時は warn のみ (例外を呼び出し側に伝播させない、UX を止めない)
 */
export function save(state: ProgressState): void {
  let json: string;
  try {
    json = JSON.stringify(state);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[nagi/progress] stringify failed:', err);
    return;
  }
  getBackend().write(json);
}

/**
 * board の評価を記録する。既存の best badge と OR 累積でマージし、`bestMoves` は
 * 「Silent 達成時のみ書き、複数回 Silent 達成時は最小値を保持」。
 *
 * @param boardId  catalog の PlayableBoard.id (TutorialBoard は対象外、§「Tutorial 仕様」)
 * @param badge    今回のプレイ結果 (engine evaluateBeauty + bestMoves を controller が組む)
 */
export function markComplete(boardId: string, badge: BestBadge): void {
  const current = load();
  const prev = current.completedBoards[boardId];
  const merged = mergeBadge(prev, badge);

  const next: ProgressState = {
    tutorialSeen: current.tutorialSeen,
    completedBoards: {
      ...current.completedBoards,
      [boardId]: merged,
    },
  };
  save(next);
}

/**
 * Tutorial 完走時に呼ぶ (W6 controller が tutorial step 4 完了で発火)。
 * 既存 progressState の completedBoards はそのまま、tutorialSeen のみ true に。
 */
export function markTutorialSeen(): void {
  const current = load();
  if (current.tutorialSeen) return;
  save({
    tutorialSeen: true,
    completedBoards: current.completedBoards,
  });
}

/** board の現在の best badge を返す (未プレイなら null) */
export function getBadge(boardId: string): BestBadge | null {
  const current = load();
  return current.completedBoards[boardId] ?? null;
}

/**
 * 与えられた archive board id 集合が「全て完了」 か判定。
 * spec §「画面フロー > ランダムボタン枯渇時」:
 *   未プレイ archive が 0 になったら「未達成から選ぶ」 文言に変える。
 *
 * 「完了」 の定義は **cleared 達成** (Silent/Clean は要求しない)。
 * Silent/Clean 未達成の「やりこみ枯渇」 は別画面 (unachieved-silent / unachieved-clean)。
 */
export function isAllArchiveCompleted(archiveBoardIds: readonly string[]): boolean {
  if (archiveBoardIds.length === 0) return false; // archive 自体が無いケースは「枯渇していない」 扱い
  const current = load();
  for (const id of archiveBoardIds) {
    const badge = current.completedBoards[id];
    if (!badge || !badge.cleared) return false;
  }
  return true;
}

/**
 * 未プレイ (= cleared でない) archive id からランダムに 1 つ選ぶ。
 * 全て完了済なら null。Title の「ランダム」 ボタン押下時に使う。
 *
 * 注意: deterministic shuffle ではなく `Math.random()` を使う (プレイヤー体験上、
 *       毎回違う盤面を引きたいだけで、再現性は要求されない)。
 */
export function pickUnplayedRandom(archiveBoardIds: readonly string[]): string | null {
  const current = load();
  const unplayed = archiveBoardIds.filter((id) => {
    const badge = current.completedBoards[id];
    return !badge || !badge.cleared;
  });
  if (unplayed.length === 0) return null;
  const idx = Math.floor(Math.random() * unplayed.length);
  return unplayed[idx] ?? null;
}

// -------------------------------------------------------------------------
// 内部ユーティリティ
// -------------------------------------------------------------------------

/**
 * Badge の OR/min マージ。
 * - cleared / clean / silent は OR 累積 (一度 true ならその後 false に落ちない)
 * - bestMoves は silent 達成時のみ意味を持つ。複数 Silent 達成時は min を取る
 *   ※ silent でない新規 badge が bestMoves を持ってきても無視 (spec: 「Silent 達成時のみ」)
 */
function mergeBadge(prev: BestBadge | undefined, next: BestBadge): BestBadge {
  if (!prev) {
    // 初回保存。silent が false なら bestMoves は持たない方が semantics きれい。
    if (next.silent && typeof next.bestMoves === 'number') {
      return {
        cleared: !!next.cleared,
        clean: !!next.clean,
        silent: true,
        bestMoves: next.bestMoves,
      };
    }
    return {
      cleared: !!next.cleared,
      clean: !!next.clean,
      silent: !!next.silent,
    };
  }

  const cleared = prev.cleared || !!next.cleared;
  const clean = prev.clean || !!next.clean;
  const silent = prev.silent || !!next.silent;

  let bestMoves: number | undefined = prev.bestMoves;
  // 今回 silent 達成かつ bestMoves が小さければ更新。silent でない次回 badge は無視。
  if (next.silent && typeof next.bestMoves === 'number') {
    if (typeof bestMoves !== 'number' || next.bestMoves < bestMoves) {
      bestMoves = next.bestMoves;
    }
  }
  // silent が一度も達成されていなければ bestMoves は保持しない
  if (!silent) {
    bestMoves = undefined;
  }

  if (typeof bestMoves === 'number') {
    return { cleared, clean, silent, bestMoves };
  }
  return { cleared, clean, silent };
}

/**
 * 任意の unknown を ProgressState に正規化する。
 * - field 欠落 → デフォルトで埋める
 * - 想定外 field は無視 (spec 外汚染を防ぐ)
 * - completedBoards の各 badge も field 単位で再構成
 */
function normalizeProgressState(raw: unknown): ProgressState {
  if (raw === null || typeof raw !== 'object') return EMPTY_STATE;
  const obj = raw as Record<string, unknown>;

  const tutorialSeen = obj['tutorialSeen'] === true;

  const rawBoards = obj['completedBoards'];
  const completedBoards: Record<string, BestBadge> = {};
  if (rawBoards !== null && typeof rawBoards === 'object') {
    for (const [boardId, badgeRaw] of Object.entries(rawBoards as Record<string, unknown>)) {
      if (typeof boardId !== 'string' || boardId === '') continue;
      const normalized = normalizeBadge(badgeRaw);
      if (normalized) completedBoards[boardId] = normalized;
    }
  }

  return {
    tutorialSeen,
    completedBoards,
  };
}

function normalizeBadge(raw: unknown): BestBadge | null {
  if (raw === null || typeof raw !== 'object') return null;
  const b = raw as Record<string, unknown>;
  const cleared = b['cleared'] === true;
  const clean = b['clean'] === true;
  const silent = b['silent'] === true;
  const bestMovesRaw = b['bestMoves'];
  let bestMoves: number | undefined;
  if (
    typeof bestMovesRaw === 'number' &&
    Number.isFinite(bestMovesRaw) &&
    Number.isInteger(bestMovesRaw) &&
    bestMovesRaw >= 0
  ) {
    bestMoves = bestMovesRaw;
  }
  if (!cleared && !clean && !silent && bestMoves === undefined) {
    // 全て空のエントリは保持しない
    return null;
  }
  if (silent && typeof bestMoves === 'number') {
    return { cleared, clean, silent, bestMoves };
  }
  return { cleared, clean, silent };
}

// -------------------------------------------------------------------------
// テスト用 (内部 only、production からは呼ばれない想定)
// -------------------------------------------------------------------------

/**
 * test 専用: backend を強制リセットして in-memory に置き換える。
 * 一般コードからは呼ばないこと (export しているのは type-level test を書きやすくするため)。
 */
export function __resetForTest(): void {
  backend = memoryBackend();
}

/** test 専用: 内部 merge ロジックを直接叩く */
export const __testInternals = Object.freeze({
  mergeBadge,
  normalizeProgressState,
  normalizeBadge,
});
